import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n/use-translation";

import { MenuFields, draftFromMenu, isValid, scheduleBody, type MenuDraft } from "./menu-fields";
import { menuLabel, type Menu } from "./menu-model";
import { useMenuActions } from "./use-menus";

/** A menu from before POS2-1456: archived on deploy as a cycle with no dates, it needs a schedule
 * before it can match waste. A seeded starter has no file and restores as it was. */
export function needsSetUp(menu: Menu): boolean {
  return menu.cadence === "cycle" && menu.starts_on === null && menu.file_type !== "seed";
}

/** "Set up and restore": the settings form, then the menu restored with that name and schedule.
 * A menu with recipes keeps them, placed on its dates; one without has its file read, which
 * `onRestored` is told so the page can show the reading (POS2-1456). */
export function SetUpDialog({
  menu,
  hasRecipes,
  menus,
  siteId,
  today,
  onClose,
  onRestored,
}: {
  menu: Menu;
  /** A menu with recipes keeps them; one without is read from its file. */
  hasRecipes: boolean;
  /** The site's menus: "Comes after", and the cycles this one would switch off. */
  menus: Menu[];
  siteId: string;
  today: string;
  onClose: () => void;
  onRestored: (reading: boolean) => void;
}) {
  const { t, locale } = useTranslation();
  const { setUpAndRestore } = useMenuActions();
  const [draft, setDraft] = useState<MenuDraft>(() => draftFromMenu(t, locale, menu, menus));
  const reads = !hasRecipes;

  function save() {
    const name = draft.name.trim();
    setUpAndRestore.mutate(
      { menuId: menu.id, siteId, displayName: name, schedule: scheduleBody(draft) },
      {
        onSuccess: () => {
          toast.success(t("menus.archived.restored", { name }));
          onRestored(reads);
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("menus.archived.setUpTitle", { name: menuLabel(t, locale, menu) })}
          </DialogTitle>
          <DialogDescription>
            {t(reads ? "menus.archived.setUpReads" : "menus.archived.setUpKeeps")}
          </DialogDescription>
        </DialogHeader>
        <MenuFields
          draft={draft}
          menus={menus}
          selfId={menu.id}
          today={today}
          onChange={setDraft}
        />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" disabled={!isValid(draft) || setUpAndRestore.isPending} onClick={save}>
            {setUpAndRestore.isPending ? t("common.saving") : t("menus.restore")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
